import {
  useId,
  type ComponentPropsWithRef,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';
import { AlertCircle, ChevronDown, Loader2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Shared with SearchableSelect, which isn't a native `<select>` but must look like one. */
export const CONTROL =
  'w-full rounded-lg border bg-white text-sm text-ink placeholder:text-ink-muted/80 transition-colors ' +
  'focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:bg-canvas disabled:text-ink-muted';

export const withError = (error?: string) =>
  error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : 'border-line';

/* -------------------------------------------------------------------------- */
/* Label + error wrapper                                                      */
/* -------------------------------------------------------------------------- */

export interface FieldProps {
  label?: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, required, error, hint, className, children }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label htmlFor={htmlFor}>
          {label}
          {required && (
            <span className="text-red-500" aria-hidden>
              {' '}
              *
            </span>
          )}
        </Label>
      )}
      {children}
      {error ? (
        <p className="flex items-start gap-1 text-xs font-medium text-red-600">
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-ink-muted">{hint}</p>
      )}
    </div>
  );
}

export function Label({ className, children, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label className={cn('text-[13px] font-semibold text-ink-soft', className)} {...rest}>
      {children}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Controls                                                                   */
/* -------------------------------------------------------------------------- */

export interface InputProps extends ComponentPropsWithRef<'input'> {
  error?: string;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
}

export function Input({ error, leftIcon, rightSlot, className, ...rest }: InputProps) {
  return (
    <div className="relative">
      {leftIcon && (
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-muted">
          {leftIcon}
        </span>
      )}
      <input
        aria-invalid={error ? true : undefined}
        className={cn(
          CONTROL,
          withError(error),
          'h-10 px-3',
          leftIcon && 'pl-9',
          rightSlot && 'pr-10',
          className,
        )}
        {...rest}
      />
      {rightSlot && (
        <span className="absolute top-1/2 right-2 -translate-y-1/2 text-ink-muted">{rightSlot}</span>
      )}
    </div>
  );
}

export interface SelectProps extends ComponentPropsWithRef<'select'> {
  error?: string;
  placeholder?: string;
}

export function Select({
  error,
  placeholder,
  loading = false,
  className,
  children,
  ...rest
}: SelectProps & { loading?: boolean }) {
  return (
    <div className="relative">
      <select
        aria-invalid={error ? true : undefined}
        aria-busy={loading || undefined}
        className={cn(
          CONTROL,
          withError(error),
          'h-10 cursor-pointer appearance-none py-0 pr-9 pl-3',
          className,
        )}
        {...rest}
      >
        {/* While the options are in flight the placeholder says so, rather than
            leaving a disabled control with no explanation for why it is dead. */}
        {placeholder && <option value="">{loading ? 'Loading…' : placeholder}</option>}
        {children}
      </select>
      {loading ? (
        <Loader2
          className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-brand-600"
          aria-hidden
        />
      ) : (
        <ChevronDown
          className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ink-muted"
          aria-hidden
        />
      )}
    </div>
  );
}

export function Textarea({
  error,
  className,
  rows = 3,
  ...rest
}: ComponentPropsWithRef<'textarea'> & { error?: string }) {
  return (
    <textarea
      rows={rows}
      aria-invalid={error ? true : undefined}
      className={cn(CONTROL, withError(error), 'resize-y px-3 py-2', className)}
      {...rest}
    />
  );
}

/** Debounce-free search box; the caller decides when to fire the query. */
export function SearchInput({
  value,
  onValueChange,
  placeholder = 'Search…',
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <Input
      type="search"
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      placeholder={placeholder}
      leftIcon={<Search className="size-4" aria-hidden />}
      className={className}
      aria-label={placeholder}
    />
  );
}

export function Checkbox({
  label,
  className,
  id,
  ...rest
}: ComponentPropsWithRef<'input'> & { label?: ReactNode }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <input
        id={inputId}
        type="checkbox"
        className="size-4 cursor-pointer rounded border-line text-brand-600 accent-brand-600 focus:ring-2 focus:ring-brand-500/25"
        {...rest}
      />
      {label && (
        <label htmlFor={inputId} className="cursor-pointer text-sm text-ink-soft select-none">
          {label}
        </label>
      )}
    </div>
  );
}

export function Toggle({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-ink">{label}</p>
        {description && <p className="mt-0.5 text-xs text-ink-muted">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50',
          checked ? 'bg-brand-600' : 'bg-slate-300',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform',
            checked && 'translate-x-5',
          )}
        />
      </button>
    </div>
  );
}
